/* Shared UI primitives */
const { useState, useMemo, useEffect, useRef } = React;

const PILL_CLASS = {
  Paid: 'paid', Open: 'open', Partial: 'partial', Overdue: 'overdue', Draft: 'draft',
  Cancelled: 'cancelled', Active: 'active', Completed: 'completed', Invoiced: 'open', Inactive: 'inactive',
  Blocked: 'overdue', 'Partly completed': 'partial', 'Ready to finish': 'active',
  Passed: 'active', Failed: 'overdue', Recorded: 'open', Skipped: 'draft',
  processed: 'active', 'in progress': 'open', unprocessed: 'draft',
};
function Pill({ children, kind, sm }) {
  const cls = PILL_CLASS[kind] || '';
  return <span className={`pill ${cls} ${sm ? 'sm' : ''}`}><span className="dot" />{children || kind}</span>;
}

// Deterministic avatar color from a string
const AVATAR_COLORS = ['#2856c4', '#0e7490', '#6d4bd0', '#b45309', '#15803d', '#9d2c5b', '#1d4ed8', '#7c5800'];
function avatarColor(s) {
  let h = 0; for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

// Markdown body rendered via window.AiChatMarkdown (resolved lazily at render time),
// with a plain-text fallback when the renderer is unavailable. `fallbackClassName`
// wraps the fallback text in a styled div instead of a bare fragment.
function MarkdownText({ text, fallbackClassName }) {
  const Renderer = window.AiChatMarkdown;
  if (Renderer) return <Renderer text={text || ''} />;
  return fallbackClassName ? <div className={fallbackClassName}>{text || ''}</div> : <React.Fragment>{text || ''}</React.Fragment>;
}

const AVATAR_PRESET_GROUPS = [
  {
    label: 'People',
    items: [
      ['ai-employee-annie', 'Annie - Animation Planner', null, '/assets/ai-employees/ai-employee-annie.png'],
      ['ai-employee-archibald', 'Archibald - Solution Architect', null, '/assets/ai-employees/ai-employee-archibald.png'],
      ['ai-employee-becky', 'Becky - Backend Developer', null, '/assets/ai-employees/ai-employee-becky.png'],
      ['ai-employee-cassie', 'Cassie - Customer Success Manager', null, '/assets/ai-employees/ai-employee-cassie.png'],
      ['ai-employee-desire', 'Desire - Designer', null, '/assets/ai-employees/ai-employee-desire.png'],
      ['ai-employee-deva', 'Deva - Senior Developer', null, '/assets/ai-employees/ai-employee-deva.png'],
      ['ai-employee-devon', 'Devon - Frontend Developer', null, '/assets/ai-employees/ai-employee-devon.png'],
      ['ai-employee-franz', 'Franz - CFO', null, '/assets/ai-employees/ai-employee-franz.png'],
      ['ai-employee-ida', 'Ida - Idea Manager', null, '/assets/ai-employees/ai-employee-ida.png'],
      ['ai-employee-knowles', 'Knowles - Knowledge Curator', null, '/assets/ai-employees/ai-employee-knowles.png'],
      ['ai-employee-plandrew', 'Plandrew - Implementation Planner', null, '/assets/ai-employees/ai-employee-plandrew.png'],
      ['ai-employee-reeves', 'Reeves - Code Review', null, '/assets/ai-employees/ai-employee-reeves.png'],
      ['ai-employee-urs', 'Urs - User Journey Architect', null, '/assets/ai-employees/ai-employee-urs.png'],
      ['ai-employee-vizzy', 'Vizzy - Lead UI/UX Designer', null, '/assets/ai-employees/ai-employee-vizzy.png'],
      ['person-man-1', 'Captain', null, '/assets/ai-employees/ai-man-1.jpg'],
      ['person-man-2', 'Construction Worker', null, '/assets/ai-employees/ai-man-2.jpg'],
      ['person-man-3', 'Chef', null, '/assets/ai-employees/ai-man-3.jpg'],
      ['person-man-4', 'Doctor', null, '/assets/ai-employees/ai-man-4.jpg'],
      ['person-man-5', 'Firefighter', null, '/assets/ai-employees/ai-man-5.jpg'],
      ['person-man-6', 'Teacher', null, '/assets/ai-employees/ai-man-6.jpg'],
      ['person-man-7', 'Farmer', null, '/assets/ai-employees/ai-man-7.jpg'],
      ['person-man-8', 'Engineer', null, '/assets/ai-employees/ai-man-8.jpg'],
      ['person-man-9', 'Musician', null, '/assets/ai-employees/ai-man-9.jpg'],
      ['person-man-10', 'Park Ranger', null, '/assets/ai-employees/ai-man-10.jpg'],
      ['person-man-11', 'Electrician', null, '/assets/ai-employees/ai-man-11.jpg'],
      ['person-man-12', 'Data Analyst', null, '/assets/ai-employees/ai-man-12.jpg'],
      ['person-man-13', 'Logistics Coordinator', null, '/assets/ai-employees/ai-man-13.jpg'],
      ['person-man-14', 'UX Researcher', null, '/assets/ai-employees/ai-man-14.jpg'],
      ['person-man-15', 'Cybersecurity Specialist', null, '/assets/ai-employees/ai-man-15.jpg'],
      ['person-man-16', 'Financial Advisor', null, '/assets/ai-employees/ai-man-16.jpg'],
      ['person-man-17', 'Marine Biologist', null, '/assets/ai-employees/ai-man-17.jpg'],
      ['person-man-18', 'Translator', null, '/assets/ai-employees/ai-man-18.jpg'],
      ['person-man-19', 'Solar Technician', null, '/assets/ai-employees/ai-man-19.jpg'],
      ['person-man-20', 'Archivist', null, '/assets/ai-employees/ai-man-20.jpg'],
      ['person-woman-1', 'Pilot', null, '/assets/ai-employees/ai-woman-1.jpg'],
      ['person-woman-2', 'Scientist', null, '/assets/ai-employees/ai-woman-2.jpg'],
      ['person-woman-3', 'Mechanic', null, '/assets/ai-employees/ai-woman-3.jpg'],
      ['person-woman-4', 'Artist', null, '/assets/ai-employees/ai-woman-4.jpg'],
      ['person-woman-5', 'Security Officer', null, '/assets/ai-employees/ai-woman-5.jpg'],
      ['person-woman-6', 'Architect', null, '/assets/ai-employees/ai-woman-6.jpg'],
      ['person-woman-7', 'Nurse', null, '/assets/ai-employees/ai-woman-7.jpg'],
      ['person-woman-8', 'Judge', null, '/assets/ai-employees/ai-woman-8.jpg'],
      ['person-woman-9', 'Developer', null, '/assets/ai-employees/ai-woman-9.jpg'],
      ['person-woman-10', 'Gardener', null, '/assets/ai-employees/ai-woman-10.jpg'],
      ['person-woman-11', 'Paramedic', null, '/assets/ai-employees/ai-woman-11.jpg'],
      ['person-woman-12', 'Product Manager', null, '/assets/ai-employees/ai-woman-12.jpg'],
      ['person-woman-13', 'Civil Engineer', null, '/assets/ai-employees/ai-woman-13.jpg'],
      ['person-woman-14', 'Accountant', null, '/assets/ai-employees/ai-woman-14.jpg'],
      ['person-woman-15', 'Robotics Technician', null, '/assets/ai-employees/ai-woman-15.jpg'],
      ['person-woman-16', 'HR Coach', null, '/assets/ai-employees/ai-woman-16.jpg'],
      ['person-woman-17', 'QA Tester', null, '/assets/ai-employees/ai-woman-17.jpg'],
      ['person-woman-18', 'Supply Chain Planner', null, '/assets/ai-employees/ai-woman-18.jpg'],
      ['person-woman-19', 'Environmental Scientist', null, '/assets/ai-employees/ai-woman-19.jpg'],
      ['person-woman-20', 'Customer Success Manager', null, '/assets/ai-employees/ai-woman-20.jpg'],
    ],
  },
  {
    label: 'Animals',
    items: [
      ['animal-cat', 'Cat', '🐈'], ['animal-dog', 'Dog', '🐕'], ['animal-fox', 'Fox', '🦊'], ['animal-bear', 'Bear', '🐻'],
      ['animal-panda', 'Panda', '🐼'], ['animal-lion', 'Lion', '🦁'], ['animal-tiger', 'Tiger', '🐯'], ['animal-monkey', 'Monkey', '🐒'],
      ['animal-koala', 'Koala', '🐨'], ['animal-rabbit', 'Rabbit', '🐇'], ['animal-owl', 'Owl', '🦉'], ['animal-whale', 'Whale', '🐋'],
      ['animal-turtle', 'Turtle', '🐢'], ['animal-frog', 'Frog', '🐸'], ['animal-penguin', 'Penguin', '🐧'], ['animal-bee', 'Bee', '🐝'],
      ['animal-butterfly', 'Butterfly', '🦋'], ['animal-crab', 'Crab', '🦀'], ['animal-octopus', 'Octopus', '🐙'], ['animal-horse', 'Horse', '🐎'],
    ],
  },
  {
    label: 'Objects',
    items: [
      ['object-laptop', 'Laptop', '💻'], ['object-key', 'Key', '🔑'], ['object-book', 'Book', '📘'], ['object-rocket', 'Rocket', '🚀'],
      ['object-compass', 'Compass', '🧭'], ['object-camera', 'Camera', '📷'], ['object-bulb', 'Bulb', '💡'], ['object-wrench', 'Wrench', '🔧'],
      ['object-mug', 'Mug', '☕'], ['object-crown', 'Crown', '👑'], ['object-gem', 'Gem', '💎'], ['object-lock', 'Lock', '🔒'],
      ['object-palette', 'Palette', '🎨'], ['object-guitar', 'Guitar', '🎸'], ['object-clock', 'Clock', '🕒'], ['object-globe', 'Globe', '🌐'],
      ['object-anchor', 'Anchor', '⚓'], ['object-briefcase', 'Briefcase', '💼'], ['object-puzzle', 'Puzzle', '🧩'], ['object-coin', 'Coin', '🪙'],
    ],
  },
  {
    label: 'Plants',
    items: [
      ['plant-leaf', 'Leaf', '🍃'], ['plant-tree', 'Tree', '🌳'], ['plant-cactus', 'Cactus', '🌵'], ['plant-flower', 'Flower', '🌸'],
      ['plant-sprout', 'Sprout', '🌱'],
    ],
  },
].map((group) => ({ ...group, items: group.items.map(([key, label, glyph, image]) => ({ key, label, glyph, image })) }));
const AVATAR_PRESETS = AVATAR_PRESET_GROUPS.flatMap((group) => group.items);
function avatarPreset(key) {
  return AVATAR_PRESETS.find((preset) => preset.key === key) || null;
}
function Avatar({ name, square, avatarKey, className = '' }) {
  const preset = avatarPreset(avatarKey);
  const style = { background: preset && preset.image ? '#dce3ea' : avatarColor(avatarKey || name), borderRadius: square ? 6 : '50%' };
  return (
    <span className={`avatar-sq ${preset && preset.image ? 'has-image' : ''} ${className}`} style={style} title={preset ? preset.label : undefined}>
      {preset && preset.image
        ? <img className="avatar-photo" src={preset.image} alt="" aria-hidden="true" />
        : preset ? <span className="avatar-glyph" aria-hidden="true">{preset.glyph}</span> : initials(name)}
    </span>
  );
}

const APPLIGATOR_TOOL_ACTIVITY_META = Object.fromEntries([
  ['action_center_approval_request', 'bell', 'Requesting approval'],
  ['action_center_questions_request', 'bell', 'Requesting your input'],
  ['action_item_list', 'list', 'Checking action inbox'],
  ['ai_employee_task_delegate', 'handover', 'Delegating work'],
  ['ai_employee_task_complete', 'check', 'Handing back delegated work'],
  ['ai_employee_task_get', 'bookOpen', 'Checking delegated work'],
  ['ai_employee_task_list_children', 'list', 'Listing delegated tasks'],
  ['cashflow_summary', 'trendUp', 'Checking cashflow'],

  ['project_list', 'list', 'Listing projects'],
  ['project_search', 'search', 'Searching projects'],
  ['project_create', 'plus', 'Creating a project'],
  ['project_update', 'edit', 'Updating a project'],
  ['project_status_update', 'flag', 'Updating project status'],
  ['project_delete', 'trash', 'Deleting a project'],
  ['project_customer_link', 'link', 'Linking a customer'],
  ['project_customer_unlink', 'unlink', 'Unlinking a customer'],
  ['customer_create', 'customers', 'Creating a customer'],
  ['customer_update', 'customers', 'Updating a customer'],
  ['customer_delete', 'trash', 'Deleting a customer'],
  ['contact_person_search', 'search', 'Searching contacts'],
  ['employee_search', 'search', 'Searching employees'],
  ['employee_create', 'employees', 'Creating an employee'],
  ['employee_update', 'employees', 'Updating an employee'],
  ['employee_delete', 'trash', 'Deleting an employee'],

  ['implementation_guideline_template_list', 'list', 'Listing implementation guidelines'],
  ['implementation_guideline_template_get', 'bookOpen', 'Reading implementation guidelines'],
  ['implementation_guideline_template_create', 'plus', 'Creating implementation guidelines'],
  ['implementation_guideline_template_update', 'edit', 'Updating implementation guidelines'],
  ['implementation_guideline_template_delete', 'trash', 'Deleting implementation guidelines'],
  ['project_implementation_guideline_templates_update', 'shield', 'Updating project guidelines'],
  ['project_design_guideline_template_list', 'list', 'Listing design guidelines'],
  ['project_design_guideline_template_get', 'palette', 'Reading design guidelines'],
  ['project_design_guideline_template_create', 'palette', 'Creating design guidelines'],
  ['project_design_guideline_template_update', 'edit', 'Updating design guidelines'],
  ['project_design_guideline_template_delete', 'trash', 'Deleting design guidelines'],
  ['project_work_package_design_guideline_templates_update', 'palette', 'Updating work package design guidelines'],

  ['project_work_package_list', 'list', 'Listing work packages'],
  ['project_work_package_create', 'plus', 'Creating a work package'],
  ['project_work_package_update', 'edit', 'Updating a work package'],
  ['project_work_package_delete', 'trash', 'Deleting a work package'],
  ['project_work_package_remove', 'trash', 'Removing a work package'],
  ['project_work_package_phase_update', 'flow', 'Updating work package phase'],
  ['project_work_packages_update', 'flow', 'Updating work packages'],
  ['project_work_package_context_get', 'bookOpen', 'Reading work package context'],
  ['project_work_package_gathered_inputs_get', 'inbox', 'Reading gathered inputs'],

  ['project_knowledge_context_get', 'bookOpen', 'Reading OKF context'],
  ['project_knowledge_concept_list', 'list', 'Listing OKF concepts'],
  ['project_knowledge_concept_get', 'bookOpen', 'Reading an OKF concept'],
  ['project_knowledge_concept_create', 'plus', 'Creating an OKF concept'],
  ['project_knowledge_concept_update', 'edit', 'Updating an OKF concept'],
  ['project_knowledge_concept_delete', 'trash', 'Deleting an OKF concept'],
  ['project_knowledge_artifact_link_create', 'link', 'Linking OKF concept'],
  ['project_knowledge_artifact_link_update', 'edit', 'Updating OKF artifact link'],
  ['project_knowledge_artifact_link_delete', 'trash', 'Deleting OKF artifact link'],
  ['project_knowledge_source_link_create', 'link', 'Linking OKF source'],
  ['project_knowledge_source_link_update', 'edit', 'Updating OKF source link'],
  ['project_knowledge_source_link_delete', 'trash', 'Deleting OKF source link'],
  ['project_source_ingest', 'inbox', 'Ingesting source context'],
  ['project_okf_export', 'download', 'Exporting OKF context'],
  ['organization_knowledge_context_get', 'bookOpen', 'Reading organization OKF context'],
  ['organization_knowledge_concept_list', 'list', 'Listing organization OKF concepts'],
  ['organization_knowledge_concept_get', 'bookOpen', 'Reading an organization OKF concept'],
  ['organization_knowledge_concept_create', 'plus', 'Creating an organization OKF concept'],
  ['organization_knowledge_concept_update', 'edit', 'Updating an organization OKF concept'],
  ['organization_knowledge_concept_delete', 'trash', 'Deleting an organization OKF concept'],
  ['organization_knowledge_artifact_link_create', 'link', 'Linking organization OKF concept'],
  ['organization_knowledge_artifact_link_update', 'edit', 'Updating organization OKF artifact link'],
  ['organization_knowledge_artifact_link_delete', 'trash', 'Deleting organization OKF artifact link'],
  ['organization_knowledge_source_link_create', 'link', 'Linking organization OKF source'],
  ['organization_knowledge_source_link_update', 'edit', 'Updating organization OKF source link'],
  ['organization_knowledge_source_link_delete', 'trash', 'Deleting organization OKF source link'],
  ['organization_source_ingest', 'inbox', 'Ingesting organization source context'],
  ['organization_okf_export', 'download', 'Exporting organization OKF context'],

  ['project_sketch_list', 'list', 'Listing sketches'],
  ['project_sketch_get', 'palette', 'Reading a sketch'],
  ['project_sketch_create', 'palette', 'Creating a sketch'],
  ['project_sketch_import', 'paperclip', 'Importing a sketch'],
  ['project_sketch_update', 'edit', 'Updating a sketch'],
  ['project_sketch_standalone_export_save', 'download', 'Exporting a sketch'],
  ['project_sketches_reorder', 'route', 'Reordering sketches'],
  ['project_sketch_delete', 'trash', 'Deleting a sketch'],

  ['project_tech_stack_list', 'list', 'Listing tech stack'],
  ['project_tech_stack_get', 'stack', 'Reading tech stack'],
  ['project_tech_stack_create', 'stack', 'Creating tech stack item'],
  ['project_tech_stack_update', 'edit', 'Updating tech stack'],
  ['project_tech_stack_delete', 'trash', 'Deleting tech stack item'],

  ['project_test_dashboard_get', 'dashboard', 'Reading test dashboard'],
  ['project_test_status_get', 'check', 'Checking test status'],
  ['project_test_suite_list', 'list', 'Listing test suites'],
  ['project_test_suite_generate', 'flow', 'Generating test suite'],
  ['project_test_suite_create', 'plus', 'Creating test suite'],
  ['project_test_suite_update', 'edit', 'Updating test suite'],
  ['project_test_suite_delete', 'trash', 'Deleting test suite'],
  ['project_test_case_create', 'plus', 'Creating test case'],
  ['project_test_case_update', 'edit', 'Updating test case'],
  ['project_test_case_delete', 'trash', 'Deleting test case'],
  ['project_test_step_create', 'plus', 'Creating test step'],
  ['project_test_step_update', 'edit', 'Updating test step'],
  ['project_test_step_delete', 'trash', 'Deleting test step'],
  ['project_test_environment_create', 'server', 'Creating test environment'],
  ['project_test_environment_update', 'server', 'Updating test environment'],
  ['project_test_environment_delete', 'trash', 'Deleting test environment'],
  ['project_test_run_queue', 'play', 'Queueing test run'],
  ['project_test_run_get', 'bookOpen', 'Reading test run'],
  ['project_test_run_cancel', 'stop', 'Canceling test run'],

  ['project_ticket_clusters_reorder', 'route', 'Reordering ticket clusters'],
  ['project_ticket_cluster_create', 'plus', 'Creating ticket cluster'],
  ['project_ticket_cluster_update', 'edit', 'Updating ticket cluster'],
  ['project_ticket_cluster_delete', 'trash', 'Deleting ticket cluster'],
  ['project_ticket_create', 'tag', 'Creating ticket'],
  ['project_ticket_update', 'edit', 'Updating ticket'],
  ['project_ticket_move', 'route', 'Moving ticket'],
  ['project_tickets_reorder', 'route', 'Reordering tickets'],
  ['project_ticket_delete', 'trash', 'Deleting ticket'],

  ['project_milestone_create', 'flag', 'Creating milestone'],
  ['project_milestone_update', 'flag', 'Updating milestone'],
  ['project_milestone_effort_update', 'clock', 'Updating milestone effort'],
  ['project_milestones_reorder', 'route', 'Reordering milestones'],
  ['project_milestone_delete', 'trash', 'Deleting milestone'],
  ['project_milestones_offer_create', 'receipt', 'Creating offer from milestones'],
  ['project_milestones_deliverables_create', 'box', 'Creating deliverables from milestones'],
  ['project_deliverable_create', 'box', 'Creating deliverable'],
  ['project_deliverable_update', 'edit', 'Updating deliverable'],
  ['project_deliverable_details_update', 'box', 'Updating deliverable details'],
  ['project_deliverable_invoices_create', 'invoices', 'Creating deliverable invoices'],
  ['project_deliverable_delete', 'trash', 'Deleting deliverable'],
  ['project_offer_create', 'receipt', 'Creating offer'],
  ['project_offer_update', 'edit', 'Updating offer'],
  ['project_offer_convert', 'box', 'Converting offer'],
  ['project_offer_delete', 'trash', 'Deleting offer'],

  ['document_list', 'list', 'Listing documents'],
  ['document_get', 'bookOpen', 'Reading document'],
  ['document_upload', 'paperclip', 'Uploading document'],
  ['document_project_attach', 'paperclip', 'Attaching document'],
  ['document_project_detach', 'unlink', 'Detaching document'],
  ['document_project_attachment_update', 'edit', 'Updating document attachment'],
  ['document_update', 'edit', 'Updating document'],
  ['document_delete', 'trash', 'Deleting document'],

  ['email_message_list', 'list', 'Listing email'],
  ['email_message_get', 'mail', 'Reading email'],
  ['email_message_status_update', 'flag', 'Updating email status'],
  ['email_project_attach', 'paperclip', 'Attaching email'],
  ['email_project_attachment_update', 'edit', 'Updating email attachment'],
  ['email_project_detach', 'unlink', 'Detaching email'],
  ['email_draft_create', 'mail', 'Drafting email'],
  ['email_draft_update', 'edit', 'Updating email draft'],
  ['email_draft_reply_to_email', 'reply', 'Drafting email reply'],
  ['email_draft_document_attach', 'paperclip', 'Attaching document to email'],

  ['note_list', 'list', 'Listing notes'],
  ['project_note_get', 'bookOpen', 'Reading note'],
  ['note_add', 'note', 'Adding note'],
  ['note_update', 'edit', 'Updating note'],
  ['note_delete', 'trash', 'Deleting note'],

  ['meeting_list', 'calendar', 'Listing meetings'],
  ['meeting_get', 'calendar', 'Reading meeting'],
  ['meeting_transcript_get', 'bookOpen', 'Reading meeting transcript'],
  ['meeting_create', 'calendar', 'Creating meeting'],
  ['meeting_update', 'edit', 'Updating meeting'],
  ['meeting_transcript_update', 'edit', 'Updating meeting transcript'],
  ['meeting_followup_create', 'plus', 'Creating meeting follow-up'],
  ['meeting_followup_update', 'edit', 'Updating meeting follow-up'],
  ['meeting_followup_delete', 'trash', 'Deleting meeting follow-up'],
  ['meeting_delete', 'trash', 'Deleting meeting'],

  ['invoice_create', 'invoices', 'Creating invoice'],
  ['invoice_update', 'edit', 'Updating invoice'],
  ['invoice_delete', 'trash', 'Deleting invoice'],
  ['invoice_finalize', 'check', 'Finalizing invoice'],
  ['invoice_cancel', 'close', 'Canceling invoice'],
  ['invoice_mark_paid', 'wallet', 'Recording invoice payment'],
  ['expense_create', 'receipt', 'Creating expense'],
  ['expense_update', 'edit', 'Updating expense'],
  ['expense_delete', 'trash', 'Deleting expense'],
].map(([toolName, icon, label]) => [toolName, { icon, label, toolName }]));

function actionCenterActivityMeta(activity) {
  const value = typeof activity === 'string' ? { toolName: activity } : (activity || {});
  const toolName = String(value.toolName || value.messageType || value.name || '').trim();
  const message = String(value.message || '').trim();
  const clean = toolName.toLowerCase();
  if (APPLIGATOR_TOOL_ACTIVITY_META[clean]) return APPLIGATOR_TOOL_ACTIVITY_META[clean];
  const readableTool = toolName
    .replace(/^mcp__[^.]+\./, '')
    .replace(/^mcp__.*__/, '')
    .replace(/[._/-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const fallbackLabel = readableTool ? `Using ${readableTool}` : (message || 'Using tool');
  const hasAny = (...parts) => parts.some((part) => clean.includes(part));
  const endsWithAny = (...parts) => parts.some((part) => clean.endsWith(part));
  if (!toolName && !message) return null;
  if (clean === 'assistant_response' || clean === 'assistant-delta' || hasAny('agentmessage/delta', 'agent_message/delta')) return { icon: 'reply', label: 'Writing response', toolName };
  if (clean === 'assistant_thinking' || hasAny('reasoning/summarypartadded', 'reasoning/summarytextdelta')) return { icon: 'activity', label: 'Thinking', toolName };
  if (hasAny('websearch', 'web_search', 'web.run', 'search_query', 'image_query', 'browse', 'browser.search')) return { icon: 'globe', label: 'Searching the web', toolName };
  if (hasAny('exec_command', 'shell', 'terminal', 'command') || clean === 'bash') return { icon: 'terminal', label: 'Running a command', toolName };
  if (hasAny('apply_patch', 'edit_file', 'file_write', 'filechange', 'file_change', 'patch') || endsWithAny('_update', '_write') || hasAny('update_', 'write_', '_edit', 'edit_')) return { icon: 'edit', label: 'Editing files', toolName };
  if (hasAny('_delete', 'delete_', '_remove', 'remove_')) return { icon: 'trash', label: 'Removing data', toolName };
  if (hasAny('_create', 'create_', '_add', 'add_')) return { icon: 'plus', label: 'Creating data', toolName };
  if (hasAny('_attach', 'attach_', '_upload', 'upload_')) return { icon: 'paperclip', label: 'Attaching files', toolName };
  if (hasAny('document', 'project_document', 'expense_document', 'file')) {
    if (hasAny('_list', 'list_', '_search', 'search_')) return { icon: 'list', label: 'Listing documents', toolName };
    if (hasAny('_read', 'read_', '_get', 'get_', '_open', 'open_')) return { icon: 'bookOpen', label: 'Reading a document', toolName };
    return { icon: 'doc', label: 'Working with documents', toolName };
  }
  if (hasAny('_list', 'list_')) return { icon: 'list', label: 'Listing records', toolName };
  if (hasAny('_search', 'search_')) return { icon: 'search', label: 'Searching records', toolName };
  if (hasAny('_read', 'read_', '_get', 'get_')) return { icon: 'bookOpen', label: 'Reading records', toolName };
  if (hasAny('email', 'mailbox', 'message')) return { icon: 'mail', label: 'Checking email', toolName };
  if (hasAny('meeting', 'calendar', 'event')) return { icon: 'calendar', label: 'Checking the calendar', toolName };
  if (hasAny('ticket')) return { icon: 'tag', label: 'Working with tickets', toolName };
  if (hasAny('project')) return { icon: 'projects', label: 'Checking project data', toolName };
  if (hasAny('employee', 'contact')) return { icon: 'users', label: 'Checking people data', toolName };
  if (hasAny('customer')) return { icon: 'customers', label: 'Checking customer data', toolName };
  if (hasAny('invoice')) return { icon: 'invoices', label: 'Checking invoices', toolName };
  if (hasAny('expense', 'payment', 'finance')) return { icon: 'wallet', label: 'Checking finance data', toolName };
  if (hasAny('test')) return { icon: 'check', label: 'Running tests', toolName };
  if (hasAny('action-center')) return { icon: 'handover', label: message || 'Updating Action Center', toolName };
  return { icon: 'tool', label: fallbackLabel, toolName };
}

function ActionCenterActivityBadge({ activity, className = '' }) {
  const meta = actionCenterActivityMeta(activity);
  if (!meta) return null;
  return (
    <span className={`ai-activity-badge ${className}`} title={meta.label} aria-hidden="true">
      <Icon name={meta.icon} size={11} stroke={2} />
    </span>
  );
}

function AiEmployeeActivityAvatar({ employee, communicating = true, className = '', title, activity }) {
  const preset = avatarPreset(employee && employee.avatarKey);
  const name = (employee && employee.name) || 'AI employee';
  const activityMeta = actionCenterActivityMeta(activity);
  return (
    <span className={`topbar-ai-avatar ${preset && preset.image ? 'has-image' : ''} ${communicating ? 'communicating' : ''} ${activityMeta ? 'has-activity' : ''} ${className}`} style={{ background: preset && preset.image ? '#dce3ea' : avatarColor((employee && (employee.avatarKey || employee.name)) || '') }} title={(activityMeta && activityMeta.label) || title || name}>
      {preset && preset.image
        ? <img className="avatar-photo" src={preset.image} alt="" aria-hidden="true" />
        : preset ? <span className="topbar-ai-avatar-glyph" aria-hidden="true">{preset.glyph}</span> : initials(name)}
      {activityMeta && <ActionCenterActivityBadge activity={activityMeta} />}
    </span>
  );
}

function ActionCenterScopeActivity({ runs, agents, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, className = '' }) {
  const activeRuns = (runs || []).filter((run) => run && !['done', 'error'].includes(String(run.status || '').trim().toLowerCase()));
  if (!activeRuns.length) return null;
  const agentById = Object.fromEntries((agents || []).map((agent) => [agent.id, agent]));
  const runsByEmployeeId = new Map();
  activeRuns.forEach((run) => {
    const employeeId = run.employeeId || run.agentId;
    if (employeeId && !runsByEmployeeId.has(employeeId)) runsByEmployeeId.set(employeeId, run);
  });
  const employeeIds = [...runsByEmployeeId.keys()];
  const shown = employeeIds.slice(0, 3);
  const shownRunIds = new Set(shown.map((employeeId) => runsByEmployeeId.get(employeeId)).filter(Boolean).map((run) => run.id));
  const overflowRuns = activeRuns.filter((run) => !shownRunIds.has(run.id));
  const openRun = (run, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const employeeId = run && (run.employeeId || run.agentId);
    if (!employeeId || !run.id) return;
    window.location.hash = `actionCenter/${encodeURIComponent(employeeId)}/${encodeURIComponent(run.id)}`;
    window.dispatchEvent(new Event('hashchange'));
  };
  const openRunFromKeyboard = (run) => (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    openRun(run, event);
  };
  return (
    <span className={`scope-ai-activity ${className}`} title={`${activeRuns.length} active Action Center task${activeRuns.length === 1 ? '' : 's'}`}>
      {shown.map((employeeId) => {
        const run = runsByEmployeeId.get(employeeId);
        const employee = agentById[employeeId] || { id: employeeId, name: 'AI employee', avatarKey: '' };
        const communicating = !!(bridgeActiveEmployeeIds && bridgeActiveEmployeeIds[String(employeeId)]);
        const activity = (bridgeActivityByRunId && run && bridgeActivityByRunId[String(run.id || '').toLowerCase()])
          || (bridgeActivityByEmployeeId && bridgeActivityByEmployeeId[String(employeeId)]);
        const activityMeta = actionCenterActivityMeta(activity);
        const title = activityMeta ? `${employee.name}: ${activityMeta.label}` : communicating ? `${employee.name} is active` : `${employee.name} has an active Action Center task`;
        return (
          <span key={employeeId} className="scope-ai-action" role="button" tabIndex={0} title={title} onClick={(event) => openRun(run, event)} onKeyDown={openRunFromKeyboard(run)}>
            <AiEmployeeActivityAvatar employee={employee} communicating={communicating} activity={activity} className="scope-ai-avatar" title={title} />
          </span>
        );
      })}
      {overflowRuns.length > 0 && <span className="scope-ai-count scope-ai-action" role="button" tabIndex={0} title={`${overflowRuns.length} more active Action Center task${overflowRuns.length === 1 ? '' : 's'}`} onClick={(event) => openRun(overflowRuns[0], event)} onKeyDown={openRunFromKeyboard(overflowRuns[0])}>+{overflowRuns.length}</span>}
    </span>
  );
}

function KPI({ label, value, delta, icon, tone, deltaTone }) {
  return (
    <div className={`kpi ${tone ? 'is-' + tone : ''}`}>
      <span className="accent" />
      <div className="label">{icon && <Icon name={icon} size={13} />}{label}</div>
      <div className="value">{value}</div>
      {delta && <div className="delta" style={deltaTone ? { color: `var(--${deltaTone})` } : null}>{delta}</div>}
    </div>
  );
}

// Horizontal proportional bar made of colored segments
function StackBar({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="aging-bar">
      {segments.map((s, i) => (
        s.value > 0 ? <span key={i} style={{ flex: s.value, background: s.color }} title={`${s.label}`}>
          {(s.value / total) > 0.1 ? s.short : ''}
        </span> : null
      ))}
    </div>
  );
}

function MiniBar({ segments }) {
  return <div className="minibar">{segments.map((s, i) => <span key={i} style={{ flex: s.value, background: s.color }} />)}</div>;
}

function Empty({ children }) { return <div className="empty">{children}</div>; }

function Toolbar({ children }) { return <div className="toolbar">{children}</div>; }

function CodexPromptButton({ prompt, children, className = 'btn ghost sm', title = 'Open in Codex', icon = 'handover', disabled, path, originUrl }) {
  const cleanPrompt = String(prompt || '').trim();
  if (!cleanPrompt || disabled) {
    return (
      <button type="button" className={className} title={title} disabled>
        <Icon name={icon} size={14} />{children}
      </button>
    );
  }
  const url = new URL('codex://new');
  url.searchParams.set('prompt', cleanPrompt);
  if (path) url.searchParams.set('path', path);
  if (originUrl) url.searchParams.set('originUrl', originUrl);
  return (
    <a className={className} title={title} href={url.toString()}>
      <Icon name={icon} size={14} />{children}
    </a>
  );
}

function normalizeContextScopeType(value) {
  return String(value || '').trim().toLowerCase().split(/[\s_:]+/).filter(Boolean).join('-');
}

function AiEmployeeContextPicker({ reference, aiEmployees, visibleEmployees, query, setQuery, contextRunsLoading, contextRuns, onOpenRun, onSelectEmployee, autoFocus = true }) {
  return (
    <React.Fragment>
      <div className="ai-context-head">
        <Icon name="server" size={14} />
        <span>Ask AI employee</span>
      </div>
      <div className="ai-context-target" title={reference && reference.label}>{reference && reference.label}</div>
      {(contextRunsLoading || contextRuns.length > 0) && (
        <div className="ai-context-runs">
          <div className="ai-context-runs-title">{contextRunsLoading ? 'Checking active tasks...' : `${contextRuns.length} active task${contextRuns.length === 1 ? '' : 's'}`}</div>
          {contextRuns.slice(0, 4).map((run) => (
            <button type="button" key={run.id} className="ai-context-run" onClick={() => onOpenRun(run)}>
              <span>{run.title || run.contextScopeLabel || 'Action Center task'}</span>
              <em>{run.status || 'queued'}</em>
            </button>
          ))}
        </div>
      )}
      <div className="search ai-context-search">
        <Icon name="search" size={14} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search employees" autoFocus={autoFocus} />
      </div>
      <div className="ai-context-list">
        {visibleEmployees.map((employee) => (
          <button type="button" key={employee.id} className="ai-context-employee" onClick={() => onSelectEmployee(employee)}>
            <Avatar name={employee.name} avatarKey={employee.avatarKey} />
            <span><strong>{employee.name}</strong><small>{employee.description || employee.role || 'AI employee'}</small></span>
          </button>
        ))}
        {aiEmployees.length === 0 && <div className="ai-context-empty">No active AI employees.</div>}
        {aiEmployees.length > 0 && visibleEmployees.length === 0 && <div className="ai-context-empty">No employees match.</div>}
      </div>
    </React.Fragment>
  );
}

function AiEmployeeContextActions({ db, onToast, onError, children }) {
  const [menu, setMenu] = useState(null);
  const [query, setQuery] = useState('');
  const [compose, setCompose] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [contextRuns, setContextRuns] = useState([]);
  const [contextRunsLoading, setContextRunsLoading] = useState(false);
  const aiEmployees = useMemo(() => (db && db.employees ? db.employees : [])
    .filter((employee) => employee.type === 'Ai' && employee.active !== false)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))), [db]);
  const visibleEmployees = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return aiEmployees;
    return aiEmployees.filter((employee) => `${employee.name || ''} ${employee.role || ''} ${employee.description || ''}`.toLowerCase().includes(q));
  }, [aiEmployees, query]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const closeOnOutsideScroll = (event) => {
      if (event.target && event.target.closest && event.target.closest('.ai-context-menu')) return;
      close();
    };
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeOnOutsideScroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeOnOutsideScroll, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  useEffect(() => {
    let cancelled = false;
    const reference = menu && menu.reference;
    const scopeType = normalizeContextScopeType(reference && reference.type);
    const scopeId = reference && reference.id ? String(reference.id).trim() : '';
    if (!reference || !scopeType || !scopeId || !window.API.listActionCenterContextRuns) {
      setContextRuns([]);
      setContextRunsLoading(false);
      return () => { cancelled = true; };
    }

    setContextRunsLoading(true);
    window.API.listActionCenterContextRuns(scopeType, scopeId)
      .then((runs) => { if (!cancelled) setContextRuns(Array.isArray(runs) ? runs : []); })
      .catch(() => { if (!cancelled) setContextRuns([]); })
      .finally(() => { if (!cancelled) setContextRunsLoading(false); });
    return () => { cancelled = true; };
  }, [menu && menu.reference && menu.reference.type, menu && menu.reference && menu.reference.id]);

  useEffect(() => {
    if (!compose) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && !busy) setCompose(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compose, busy]);

  const menuPositionFromEvent = (event, anchored) => {
    const width = 290;
    const menuHeight = 330;
    const margin = 9;
    const maxX = Math.max(margin, window.innerWidth - width - margin);
    const maxY = Math.max(margin, window.innerHeight - menuHeight - margin);
    if (anchored && event.currentTarget && event.currentTarget.getBoundingClientRect) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = Math.min(Math.max(margin, rect.right - width), maxX);
      const y = Math.min(Math.max(margin, rect.bottom + 6), maxY);
      return { x, y };
    }
    return {
      x: Math.min(Math.max(margin, event.clientX), maxX),
      y: Math.min(Math.max(margin, event.clientY), maxY),
    };
  };
  const openContextActions = (event, reference, anchored) => {
    if (!reference) return;
    event.preventDefault();
    event.stopPropagation();
    const position = menuPositionFromEvent(event, anchored);
    setQuery('');
    setMenu({
      x: position.x,
      y: position.y,
      reference,
    });
  };
  const openContextMenu = (event, reference) => openContextActions(event, reference, false);
  const openContextPicker = (event, reference) => openContextActions(event, reference, true);
  const contextMenuProps = (reference) => ({
    onContextMenu: (event) => openContextMenu(event, reference),
  });
  const openComposer = (employee) => {
    if (!menu || !employee) return;
    setCompose({ employee, reference: menu.reference });
    setPrompt('');
    setMenu(null);
  };
  const openActionCenterRun = (run) => {
    if (!run || !run.employeeId || !run.id) return;
    window.location.hash = `actionCenter/${encodeURIComponent(run.employeeId)}/${encodeURIComponent(run.id)}`;
    window.dispatchEvent(new Event('hashchange'));
    setMenu(null);
  };
  const referenceFields = (reference) => {
    if (!reference || !reference.fields) return [];
    return Object.entries(reference.fields)
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : String(value)]);
  };
  const actionCenterReference = (reference) => ({
    type: reference.type || 'Item',
    id: reference.id || '',
    label: reference.label || 'Untitled',
    url: reference.url || '',
    fields: Object.fromEntries(referenceFields(reference)),
  });
  const submit = async () => {
    const clean = prompt.trim();
    if (!compose || !clean || busy) return;
    try {
      setBusy(true);
      await window.API.startActionCenterContextTask(compose.employee.id, clean, actionCenterReference(compose.reference));
      if (onToast) onToast('Action Center task queued');
      setCompose(null);
      setPrompt('');
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setBusy(false);
    }
  };
  const submitOnEnter = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  return (
    <React.Fragment>
      {children({ contextMenuProps, openContextMenu, openContextPicker })}
      {menu && (
        <div className="ai-context-menu" style={{ left: menu.x, top: menu.y }} onMouseDown={(event) => event.stopPropagation()}>
          <AiEmployeeContextPicker
            reference={menu.reference}
            aiEmployees={aiEmployees}
            visibleEmployees={visibleEmployees}
            query={query}
            setQuery={setQuery}
            contextRunsLoading={contextRunsLoading}
            contextRuns={contextRuns}
            onOpenRun={openActionCenterRun}
            onSelectEmployee={openComposer}
          />
        </div>
      )}
      {compose && (
        <React.Fragment>
          <div className="ai-compose-backdrop" onClick={() => !busy && setCompose(null)} />
          <section className="ai-compose-modal" role="dialog" aria-modal="true" aria-labelledby="ai-compose-title">
            <header>
              <Avatar name={compose.employee.name} avatarKey={compose.employee.avatarKey} />
              <div>
                <h2 id="ai-compose-title">Message {compose.employee.name}</h2>
                <p>{compose.reference.type || 'Item'} · {compose.reference.label || 'Untitled'}</p>
              </div>
              <button className="icon-btn" type="button" title="Close" onClick={() => !busy && setCompose(null)} disabled={busy}><Icon name="close" size={16} /></button>
            </header>
            <div className="body">
              <div className="ai-compose-reference">
                <span>{compose.reference.type || 'Item'}</span>
                <strong>{compose.reference.label || 'Untitled'}</strong>
              </div>
              <div className="field">
                <label>Prompt</label>
                <textarea className="inp ai-compose-textarea" value={prompt} onChange={(event) => setPrompt(event.target.value)} onKeyDown={submitOnEnter} placeholder="What should this employee do with this context?" autoFocus />
              </div>
            </div>
            <footer>
              <button className="btn ghost" type="button" onClick={() => setCompose(null)} disabled={busy}>Cancel</button>
              <button className="btn" type="button" onClick={submit} disabled={busy || !prompt.trim()}><Icon name="send" size={14} />{busy ? 'Sending...' : 'Send'}</button>
            </footer>
          </section>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="segmented">
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return <button key={v} className={v === value ? 'on' : ''} onClick={() => onChange(v)}>{l}</button>;
      })}
    </div>
  );
}

function YearStepper({ year, onChange }) {
  return (
    <div className="stepper">
      <button onClick={() => onChange(year - 1)}><Icon name="chevL" size={14} /></button>
      <span className="yr">{year}</span>
      <button onClick={() => onChange(year + 1)}><Icon name="chevR" size={14} /></button>
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="search">
      <Icon name="search" size={14} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || 'Search…'} />
    </div>
  );
}

function Panel({ title, sub, actions, children, flush, style, className }) {
  return (
    <section className={`panel ${className || ''}`} style={style}>
      {(title || actions) && (
        <div className="panel-head">
          <div>
            <h2>{title}</h2>
            {sub && <div className="sub">{sub}</div>}
          </div>
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      <div className={`panel-body ${flush ? 'flush' : ''}`}>{children}</div>
    </section>
  );
}

function ConfigurableDetailModal({ open = true, title, kicker, subtitle, icon = 'bell', tone = 'default', summaryLabel, summary, badges, rows, actions, onClose, className, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event) => { if (event.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const modalActions = actions && actions.length ? actions : [{ key: 'close', label: 'Close', variant: 'ghost', onClick: onClose }];
  const modalRows = rows || [];
  const modalBadges = badges || [];
  const titleId = 'configurable-detail-modal-title';

  return (
    <div className="cdm-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && onClose) onClose(); }}>
      <section className={`cdm-modal ${className || ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className={`cdm-head cdm-${tone}`}>
          <span className="cdm-icon"><Icon name={icon} size={22} /></span>
          <div className="cdm-title">
            {kicker && <span className="cdm-kicker">{kicker}</span>}
            <h2 id={titleId}>{title || 'Details'}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {onClose && <button className="icon-btn" type="button" title="Close" onClick={onClose}><Icon name="close" size={16} /></button>}
        </header>
        <div className="cdm-body">
          {(summary || modalBadges.length > 0) && (
            <div className="cdm-summary">
              <div>
                {summaryLabel && <span className="cdm-label">{summaryLabel}</span>}
                {summary && <p>{summary}</p>}
              </div>
              {modalBadges.length > 0 && <div className="cdm-pills">{modalBadges.map((badge, index) => <React.Fragment key={badge.key || index}>{badge}</React.Fragment>)}</div>}
            </div>
          )}
          {children}
          {modalRows.length > 0 && (
            <div className="cdm-grid">
              {modalRows.map((row) => (
                <div className="cdm-row" key={row.key || row.label}>
                  <span>{row.label}</span>
                  <strong>{row.value || <span className="muted">-</span>}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
        <footer className="cdm-foot">
          {modalActions.map((action) => (
            <button key={action.key || action.label} className={`btn ${action.variant === 'ghost' ? 'ghost' : ''}`} type="button" onClick={action.onClick}>
              {action.icon && <Icon name={action.icon} size={14} />}{action.label}
            </button>
          ))}
        </footer>
      </section>
    </div>
  );
}

function TicketRemarksDrawer({ item, categoryName, open, onClose, onSave }) {
  const [remarks, setRemarks] = useState('');
  useEffect(() => {
    if (open) setRemarks((item && item.remarks) || '');
  }, [open, item && item.id]);

  if (!open || !item) return null;

  const title = item.title || 'Untitled ticket';
  return (
    <React.Fragment>
      <div className="overlay ticket-remarks-overlay" onClick={onClose} />
      <aside className="drawer ticket-remarks-drawer">
        <header>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>Ticket remarks</h2>
            <div className="sub">{categoryName || item.category || 'Ticket'} · {title}</div>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="close" size={17} /></button>
        </header>
        <div className="body">
          <div className="field">
            <label>Remarks</label>
            <textarea className="inp ticket-remarks-area" autoFocus rows="12" value={remarks} onChange={(event) => setRemarks(event.target.value)} placeholder="Add implementation notes, context, or handoff details." />
          </div>
        </div>
        <footer>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => onSave(remarks)}><Icon name="check" size={14} />Apply remarks</button>
        </footer>
      </aside>
    </React.Fragment>
  );
}

Object.assign(window, { Pill, Avatar, AiEmployeeActivityAvatar, ActionCenterActivityBadge, actionCenterActivityMeta, ActionCenterScopeActivity, avatarColor, initials, MarkdownText, avatarPreset, AVATAR_PRESET_GROUPS, KPI, StackBar, MiniBar, Empty, Toolbar, CodexPromptButton, AiEmployeeContextPicker, AiEmployeeContextActions, Segmented, YearStepper, SearchBox, Panel, ConfigurableDetailModal, TicketRemarksDrawer });
